import { Component, ViewEncapsulation, OnInit, ViewChild, ElementRef } from '@angular/core';
import * as _ from 'lodash';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { isNumber } from '../../../models/utils';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { Endpoint, HelpIndex, MessageType, ModalTargetType, TagOperationType, TagState, UpdateStatusType } from '../../../models/enums';
import { TagApplication } from '../../../models/tag';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { TagsModalComponent, TagsModalData } from '../tags-modal/tags-modal.component';
import { Permissions, ObjectActionListType, ActionType } from '../../../models/permission';
import { PopoverDirective } from 'ngx-bootstrap/popover';

export interface ReprocessModalData {
    selectedNode: GenericTreeNode;
    targetType: ModalTargetType;
    targets: Array<string>;
    updateStatusType?: UpdateStatusType;
}

@Component({
    standalone: false,
    selector: 'res-reprocess',
    templateUrl: './reprocess-modal-modal.component.html',
    styleUrls: ['./reprocess-modal-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ReprocessModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild('rangeInfoPopover') rangeInfoPopover: PopoverDirective;
    @ViewChild('msgProcessingTimeout') msgProcessingTimeout: ElementRef;
    @ViewChild('docRangeInfoPopover') docRangeInfoPopover: PopoverDirective;
    @ViewChild('docProcessingTimeout') docProcessingTimeout: ElementRef;

    automationId = 'reprocess';
    modalClass = 'reprocess-modal';
    modalHelpIndex = HelpIndex.reprocess_options_dlg;
    isBusy = false;
    isBusyMessage: string;

    reprocessModalData: ReprocessModalData;
    documentReprocessTimeout = 10;
    documentsOnly = true;
    extractFromContainer = false;
    syncChildrenToProjectData = true;
    applyParentTags = true;
    applyNewTags = false;
    selectedTags = new Array<TagApplication>();
    msgTimeout: number;
    defaultMsgTimeout: number;
    hasTagPermissions: boolean;
    rangeInfoText: string;
    docRangeInfoText: string;
    msgArchivetimeoutChangedToDefaultRange = false;
    docProcessingTimeoutChangedToDefaultRange = false;

    constructor(
        private modalService: BsModalService,
        public deleteEntityRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService) {
        super(modalService, deleteEntityRef);
    }

    ngOnInit(): void {
        this.reprocessModalData = this.modalData as ReprocessModalData;
        this.hasTagPermissions = Permissions.checkUserPermission(ObjectActionListType.DOCUMENT_TAGS, ActionType.CREATE)
            && Permissions.checkUserPermission(ObjectActionListType.PRJ_TAGS);
        this.restService.post(Endpoint.projectManager_getIndexSettings, { handle: this.commonService.currentProject.handle }).then(response => {
            if (response?.settings) {
                this.msgTimeout = response.settings.archivalProcessingTimeout;
                this.defaultMsgTimeout = response.settings.archivalProcessingTimeout;
            }
        });
    }

    selectTags(): void {
        this.commonService.openModal(TagsModalComponent, {
            modalData: <TagsModalData>{
                selectedTags: this.selectedTags,
                operationType: TagOperationType.select
            }
        }).then(response => {
            if (response && response.modalResult && response.modalData) {
                this.selectedTags = (response.modalData as Array<TagApplication>).filter(ta => ta.tagState === TagState.ON);
            }
        });
    }

    okEnabled(): boolean {
        return this.reprocessModalData && this.reprocessModalData.selectedNode && this.reprocessModalData.targets && this.reprocessModalData.targets.length > 0;
    }

    restrictNumeric = (event): boolean => {
        if (event) {
            return (event.charCode === 8 || event.charCode === 0) ? false : event.charCode >= 48 && event.charCode <= 57;
        }
        return false;
    }

    showInfoPopover(modelName: string, infoText: string) {
        this.docRangeInfoPopover?.hide();
        this.rangeInfoPopover?.hide();
        this.msgArchivetimeoutChangedToDefaultRange = false;
        this.docProcessingTimeoutChangedToDefaultRange = false;
        if (modelName === 'msgTimeout') {
            this.rangeInfoText = infoText;
            this.rangeInfoPopover.show();
            this.msgArchivetimeoutChangedToDefaultRange = true;
        } else if (modelName === 'documentReprocessTimeout') {
            this.docRangeInfoText = infoText;
            this.docRangeInfoPopover.show();
            this.docProcessingTimeoutChangedToDefaultRange = true;
        }
    }

    parseNumeric = (modelName: string, min?: number, max?: number): void => {
        if (modelName) {
            let field = _.get(this, modelName);
            if (field || (field === '')) {
                if (field === '') {
                    const text = 'This field must not be blank. The current project setting value has been entered.';
                    field = modelName === 'msgTimeout' ? this.defaultMsgTimeout : 0;
                    this.showInfoPopover(modelName, text);
                }

                let val = parseInt(field, 10);
                let text = '';
                if (isNaN(val)) {
                    return;
                }
                if (isNumber(min) && min > val) {
                    val = min;
                    text = 'The value must be between ' + min + ' and ' + max + '. It has been changed to ' + min + '.';
                    this.showInfoPopover(modelName, text);
                }
                if (isNumber(max) && val > max) {
                    val = max;
                    text = 'The value must be between ' + min + ' and ' + max + '. It has been changed to ' + max + '.';
                    this.showInfoPopover(modelName, text);
                }
                _.set(this, modelName, val);
            }
        }
    }

    ok(): void {
        // If Message Archive Processing Timeout is not within the range of 10-1200 and 'OK' is clicked onBlur
        // the focus should be changed to the timeout input field
        let fieldToFocus: HTMLElement;

        if (this.msgArchivetimeoutChangedToDefaultRange) {
            fieldToFocus = (this.msgProcessingTimeout.nativeElement as HTMLElement);
        }

        // If Document Processing Timeout is not within the range of 3-180 and 'OK' is clicked onBlur
        // the focus should be changed to the document processing timeout input field
        if (this.docProcessingTimeoutChangedToDefaultRange) {
            fieldToFocus = (this.docProcessingTimeout.nativeElement as HTMLElement);
        }

        if (fieldToFocus) {
            fieldToFocus.focus();
            this.msgArchivetimeoutChangedToDefaultRange = false;
            this.docProcessingTimeoutChangedToDefaultRange = false;
            return;
        }

        this.docRangeInfoPopover.hide();
        this.rangeInfoPopover?.hide();

        const reqMsg: any = {
            reprocessSuccessful: true,
            respectOcrConfig: false,
            documentReprocessTimeout: this.documentReprocessTimeout
        };
        if (this.documentsOnly) {
            reqMsg.reprocessDocumentsOnly = true;
            reqMsg.extractDocumentsFromContainer = this.extractFromContainer;
        } else {
            reqMsg.reprocessDocumentsWithChildren = true;
            reqMsg.syncDocumentChildrenToCaseData = this.syncChildrenToProjectData;
            if (this.syncChildrenToProjectData) {
                if (this.applyNewTags && this.selectedTags.length > 0) {
                    reqMsg.newChildrenTags = this.selectedTags;
                }
                reqMsg.syncDocumentChildrenTagState = this.applyParentTags;
            }
            reqMsg.messageArchiveReprocessTimeout = this.msgTimeout;
        }
        this.isBusyMessage = 'Reprocessing...';
        this.isBusy = true;
        const viewHandle = this.commonService.matrixGridsSelection?.openFromDocumentActionBtn ? this.commonService.matrixGridsSelection?.viewHandle : this.reprocessModalData.selectedNode?.data?.handle;
        const viewName = (this.commonService.matrixGridsSelection?.openFromDocumentActionBtn && this.commonService.matrixGridsSelection?.viewName) ? this.commonService.matrixGridsSelection?.viewName : this.reprocessModalData?.selectedNode?.name;
        this.restService.post(Endpoint.viewManager_canReprocess, { handle: viewHandle }).then(responseCanReprocess => {
            if (responseCanReprocess && responseCanReprocess.percentComplete === 100) {
                this.restService.post(Endpoint.viewManager_canReprocessResult, { handle: responseCanReprocess.handle }).then(responseCanReprocessResult => {
                    this.isBusy = false;
                    if (responseCanReprocessResult) {
                        if (responseCanReprocessResult.valid) {
                            let endpoint: Endpoint;
                            if (this.reprocessModalData.targetType === ModalTargetType.document) {
                                reqMsg.documentHandleList = this.reprocessModalData.targets;
                                reqMsg.taskDescription = 'Reprocessing documents from view ' + viewName;
                                endpoint = Endpoint.corpusManager_reprocessFromDocuments;
                            } else {
                                reqMsg.viewHandle = this.reprocessModalData.targets[0];
                                reqMsg.taskDescription = 'Reprocessing all documents from view ' + viewName;
                                endpoint = Endpoint.corpusManager_reprocessFromView;
                            }
                            this.restService.post(endpoint, reqMsg, true,
                                this.reprocessModalData.updateStatusType ? [MessageType.entityChanged, this.reprocessModalData.updateStatusType] : true);
                            this.closeModal();
                        } else {
                            this.commonService.openWarningModal(responseCanReprocessResult.reason);
                        }
                    }
                }, error => {
                    this.isBusy = false;
                });
            }
        }, error => {
            this.isBusy = false;
        });
    }
}
